/**
 * @file: useLoadLatestDraft.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { delayed } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useEffect, useRef } from 'react';
import { useCheckExistingOrder } from './useCheckExistingOrder';

export function useLoadLatestDraft() {
  const { draftManager } = useOrderContext();
  const { loadDraft, draftId, mode, type } = useOrderFlowContext();
  const [drafts] = useBehaviorSubject(draftManager.drafts$);
  const initialized = useRef(false);
  const draftsLoaded = drafts != null;
  const initialDraftId = useRef(draftId).current;
  const checkExistingOrder = useCheckExistingOrder();
  const confirm = useConfirm();

  const tryApplyLatestDraft = usePersistFn(async () => {
    if (mode === 'update' || type === 'child') return;
    const latestDraft = draftManager.getLatestDraft(initialDraftId);
    if (latestDraft != null) {
      if (
        await confirm({
          title: '提示',
          message: `存在最近车牌为${latestDraft.form.licensePlateNo}的建单草稿，是否立即加载?`,
        })
      ) {
        loadDraft(latestDraft);
        if (latestDraft.form.licensePlateNo) {
          void checkExistingOrder('plate', latestDraft.form.licensePlateNo);
        } else if (latestDraft.form.vin) {
          void checkExistingOrder('vin', latestDraft.form.vin);
        }
      }
    }
  });

  useEffect(() => {
    if (!draftsLoaded || initialized.current) return;
    delayed(tryApplyLatestDraft, 300);
    initialized.current = true;
  }, [draftsLoaded, tryApplyLatestDraft]);
}
